import {useLocation} from 'react-router';
import {useRef} from 'react';
import {useDropdown} from '../hooks/useDropdown';
import {languageFromPath, localizePath, SiteLanguage} from './index';

/**
 * Le sélecteur de langue de la barre — une liste, pas un `<select>` natif.
 *
 * ## Pourquoi avoir quitté le natif
 *
 * La liste déroulante d'un `<select>` est peinte par le système : ni sa bordure, ni son fond, ni
 * l'espacement de ses options ne se stylent, et le rendu diffère sur les trois moteurs. Le design
 * demande des options encadrées, empilées, à la typographie du site — donc une liste qu'on rend
 * soi-même.
 *
 * Ce que le natif offrait gratuitement est alors à écrire : le clavier, le nom accessible, la
 * fermeture au clic extérieur. Les deux premiers vivent ici, le troisième dans `useDropdown` avec
 * les deux autres fermetures qu'on oublie — Échap, et la sortie du focus.
 *
 * ## Ce que le motif `listbox` exige
 *
 * `aria-haspopup="listbox"` et `aria-expanded` sur le déclencheur ; `role="listbox"` sur la
 * liste, `role="option"` et `aria-selected` sur chaque entrée. Les options ne sont pas des liens :
 * un lecteur d'écran annoncerait « lien » là où le geste choisit une langue.
 *
 * ## Le changement recharge
 *
 * Le `<html lang>`, le titre, la canonique et les alternatives viennent du HTML pré-rendu de
 * chaque URL, et le loader du widget lit `documentElement.lang` en se posant. Recharger met les
 * trois d'accord d'un seul geste, là où une navigation cliente les laissait à rattraper.
 */
const CODES: Record<SiteLanguage, string> = {fr: 'FR', en: 'EN'};
const LANGUES = Object.keys(CODES) as SiteLanguage[];

export default function LanguageSwitcher() {
    const {pathname, search, hash} = useLocation();
    const locale = languageFromPath(pathname);
    const {open, setOpen, close, rootRef, triggerRef} = useDropdown();
    const optionsRef = useRef<(HTMLLIElement | null)[]>([]);

    const choisir = (langue: SiteLanguage) => {
        if (langue === locale) return close(true);
        window.location.assign(localizePath(pathname + search + hash, langue));
    };

    const ouvrirSur = (index: number) => {
        setOpen(true);
        // Le focus part sur l'option visée au rendu suivant : la liste n'existe pas encore.
        requestAnimationFrame(() => optionsRef.current[index]?.focus());
    };

    const surTouche = (event: React.KeyboardEvent, index: number) => {
        const dernier = LANGUES.length - 1;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const suivant = event.key === 'ArrowDown'
                ? (index === dernier ? 0 : index + 1)
                : (index === 0 ? dernier : index - 1);
            optionsRef.current[suivant]?.focus();
            return;
        }
        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            optionsRef.current[event.key === 'Home' ? 0 : dernier]?.focus();
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choisir(LANGUES[index]);
        }
    };

    return <div className="lang-menu" ref={rootRef}>
        <button ref={triggerRef} type="button" className="lang-menu__trigger" aria-haspopup="listbox"
            aria-expanded={open} aria-label={locale === 'fr' ? 'Langue' : 'Language'}
            onClick={() => (open ? close() : setOpen(true))}
            onKeyDown={event => {
                if (event.key === 'ArrowDown') { event.preventDefault(); ouvrirSur(0); }
                if (event.key === 'ArrowUp') { event.preventDefault(); ouvrirSur(LANGUES.length - 1); }
            }}>
            <span>{CODES[locale]}</span>
            <svg className="lang-menu__chevron" viewBox="0 0 12 8" aria-hidden="true">
                <path d="M1 1.5 6 6.5l5-5" fill="none" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </button>

        {open && <ul className="lang-menu__list" role="listbox"
            aria-label={locale === 'fr' ? 'Langue' : 'Language'}>
            {LANGUES.map((langue, index) => <li key={langue} role="option" lang={langue}
                aria-selected={langue === locale} tabIndex={-1}
                ref={element => { optionsRef.current[index] = element; }}
                className="lang-menu__option"
                onClick={() => choisir(langue)}
                onKeyDown={event => surTouche(event, index)}>
                {CODES[langue]}
            </li>)}
        </ul>}
    </div>;
}
